import { useMutation, useQuery } from "@tanstack/react-query";
import type { ImageZipApiParams, ImageZipJobApiParams } from "./api";
import { downloadImagesApi } from "./api";
import { type ImageZipJobResponse, getImageZipProcessRateNumber } from "./schemas";

export const downloadImagesQueryKeys = {
  all: ["download-images"] as const,
  imageZipFile: ({ projectId, dailyLogId }: ImageZipApiParams) =>
    [...downloadImagesQueryKeys.all, "image-zip-file", projectId, dailyLogId] as const,
  imageZipJob: ({ projectId, dailyLogId, jobId }: ImageZipJobApiParams) =>
    [...downloadImagesQueryKeys.all, "image-zip-job", projectId, dailyLogId, jobId] as const,
};

const POLLING_INTERVAL_MS = 1_500;

const shouldPollImageZipJob = (job?: ImageZipJobResponse) => {
  if (!job) return true;
  if (job.status !== "PENDING" && job.status !== "IN_PROGRESS") return false;
  const processRate = getImageZipProcessRateNumber(job.progress_rate);
  if (processRate !== null && processRate >= 100) return false;
  return true;
};

export const useImageZipFileQuery = (params: ImageZipApiParams) => {
  return useQuery({
    queryKey: downloadImagesQueryKeys.imageZipFile(params),
    queryFn: () => downloadImagesApi.getImageZipFile(params),
    enabled: Boolean(params.projectId && params.dailyLogId),
  });
};

export const useCreateImageZipJobMutation = () => {
  return useMutation({
    mutationFn: (params: ImageZipApiParams) => downloadImagesApi.createImageZipJob(params),
  });
};

export const useImageZipJobQuery = (params: ImageZipJobApiParams | null, options?: { stopPolling?: boolean }) => {
  return useQuery({
    queryKey: params
      ? downloadImagesQueryKeys.imageZipJob(params)
      : [...downloadImagesQueryKeys.all, "image-zip-job", null],
    queryFn: () => {
      if (!params) throw new Error("image zip job params are required.");
      return downloadImagesApi.getImageZipJob(params);
    },
    enabled: Boolean(params?.projectId && params?.dailyLogId && params?.jobId),
    refetchInterval: options?.stopPolling
      ? false
      : (query) => {
          const job = query.state.data;
          return shouldPollImageZipJob(job) ? POLLING_INTERVAL_MS : false;
        },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useCancelImageZipJobMutation = () => {
  return useMutation({
    mutationFn: (params: ImageZipJobApiParams) => downloadImagesApi.cancelImageZipJob(params),
  });
};
